import type { MainPhotoRef } from '../../../shared/product'
import type { BarcodeLookupVm } from './scanner'

/**
 * Модель фичи «Поиск по фото» (RP-UA1b). ML-смысл не меняется: фасад Platform
 * ведёт ту же визуальную дорожку; экран показывает ответ, ничего не пересчитывая.
 * Ищет только в боевом каталоге: тестовый каталог замеров — забота будущего
 * раздела «Замеры поиска» (UI-MEASURE), а не переключатель в поиске (RP-FB27).
 */

export interface SearchIdentity {
  resourceId: string
  resourceKind: string
  entityKind: string
  remoteKey: string
  /** `observed` | `deleted` | `null` — Platform состояния не видела. */
  state: string | null
}

/**
 * Вклад одной дорожки в уверенность (`Platform.Contracts.Search.ConfidencePart`, RP-FB54), только в ответе «Тестирования»: `lane` —
 * `visual` | `text` | `barcode`; `points` — доля итоговой уверенности 0…1; `evidence` — сила самого свидетельства дорожки 0…1.
 */
export interface ConfidencePart {
  lane: string
  points: number
  evidence: number
}

/**
 * Что сказало фото об этом кандидате (выводится из `photoChecked` и кода свидетельства «фото», RP-FB65/FB66): `checked` — сходство по фото
 * было и учтено; `arbitrated` — фото различило варианты серии; `absent` — сходства по фото нет (у товара нет фото в базе или оно сильно
 * отличается), сверять было нечем — это не довод ни за, ни против; `indistinct` — фото есть у всех вариантов, но выглядят они одинаково;
 * `incomplete` — фото есть не у всех вариантов серии, и между ними оно не сравнивалось.
 */
export type PhotoCheck = 'checked' | 'arbitrated' | 'absent' | 'indistinct' | 'incomplete'

/**
 * Одно свидетельство о кандидате (`Platform.Contracts.Search.EvidenceItem`): `kind` — `barcode` | `text` | `numbers` | `photo`; `logLr` —
 * натуральный логарифм отношения правдоподобия (больше 0 — довод «за», меньше 0 — «против»); `present: false` — по этой дорожке данных нет
 * (не прочитано, нечего сравнивать): это «нет данных», вклад 0, а не минус; `note` — КОД пояснения (`text-strong`, `numbers-mismatch`,
 * `photo-missing-in-group`…), фраз сервер не присылает: текст по коду собирает клиент (`evidenceLabels.ts`).
 */
export interface EvidenceItem {
  kind: string
  present: boolean
  logLr: number
  note: string
}

/**
 * Серия товаров, которых текст не различил (`TwinGroupInfo`, RP-FB65), как её видит одна позиция: `id` общий у всех членов серии, `size` —
 * сколько их всего, `differences` — слова и числа названия (в нижнем регистре), которых нет хотя бы у одного другого члена.
 */
export interface TwinGroup {
  id: string
  size: number
  differences: readonly string[]
}

export interface SearchHit {
  productId: string
  name: string
  sku: string | null
  barcode: string | null
  /** Процент совпадения 0…1 (шаг 0,01): по нему упорядочена выдача; 1 — только при полном совпадении по фото, тексту и штрихкоду. */
  confidence: number
  matchedBy: readonly string[]
  identities: readonly SearchIdentity[]
  /** У позиции было сходство по фото; нет поля — сервер прежний (RP-FB65), подписи про фото нет. */
  photoChecked?: boolean | undefined
  /** Текст и фото указывают на разные товары (RP-FB65); нет поля — сервер прежний. */
  disagrees?: boolean | undefined
  /** Серия близнецов этой позиции; `null` — позиция не в серии; нет поля — сервер прежний. */
  twinGroup?: TwinGroup | null | undefined
  /** Из чего сложилась уверенность; нет поля — сервер прежний, экран показывает только число. */
  evidence?: readonly EvidenceItem[] | undefined
  /** Главное фото ОБЩЕГО товара (RP-FB70): миниатюра берётся у Platform; `null` — своих фото нет; нет поля — сервер прежний. */
  mainPhoto?: MainPhotoRef | null | undefined
}

/** Прочитанное на снимке: текст этикетки (`null` — текста не нашлось) и штрихкоды. На выдачу не влияет — только показ. */
export interface PhotoRecognition {
  text: string | null
  barcodes: readonly string[]
  /** Строки с размером и весом в порядке чтения (`Platform.Contracts.Search.RecognizedLine`); пусто — старый сервер. */
  lines: readonly RecognizedLine[]
}

/** Полоса веса строки: ключевая (крупная), средняя, мелкая, не участвует в поиске. */
export const LINE_BANDS = ['key', 'medium', 'minor', 'ignored'] as const
export type LineBand = (typeof LINE_BANDS)[number]

export const isLineBand = (value: string): value is LineBand => LINE_BANDS.some((band) => band === value)

export interface RecognizedLine {
  text: string
  confidence: number
  inRoi: boolean
  /** Вес 0…1: чем крупнее строка относительно самой крупной, тем важнее. */
  weight: number
  band: LineBand
  /** Почему строка отброшена («слишком короткая (1 зн.), уверенность 0,41»); у годной строки и у прежнего сервера — нет. */
  ignoredReason?: string | null | undefined
  /** Строку прочитал прицельный проход (крупная область перечитана с увеличением): так находят цифру серии («Fix 2»). Нет поля — обычное чтение. */
  focus?: boolean | undefined
}

/** Маленькая картинка для показа (JPEG в base64), живёт только в ответе поиска. */
export interface PhotoPreview {
  contentType: string
  dataBase64: string
}

/**
 * Как обработан кадр запроса (`Platform.Contracts.Search.PhotoProcessing`, RP-FB55): что ушло в энкодер. `null` в поле — этого шага не было
 * или сервер не прислал превью.
 */
export interface PhotoProcessing {
  sourceWidth: number | null
  sourceHeight: number | null
  sourceBytes: number
  backgroundRemoved: boolean
  /** Одна фраза: «Рамка товара не искалась: …» / «Кадр вырезан службой media по рамке товара…». */
  backgroundNote: string
  /** Кадр для поиска (рамка товара с фоном, превью), если кадр готовился. */
  processedPreview: PhotoPreview | null
  /** Рамка в долях кадра `[x0, y0, x1, y1]`; `null` — кадр целиком. */
  roi: readonly number[] | null
  cropWidth: number | null
  cropHeight: number | null
  encoder: string | null
  /** `pad_resize` — паддинг до квадрата и масштаб; `hf_processor` — процессор модели. */
  preprocessingKind: string | null
  inputSize: number | null
  padColor: readonly number[] | null
  encoderInputPreview: PhotoPreview | null
  /** Код причины, почему нет кадра для поиска (`PhotoPreviewReasons`, RP-FB58); нет поля — прежний сервер. */
  processedPreviewReason?: string | null | undefined
  /** Код причины, почему нет картинки «что видит энкодер». */
  encoderInputPreviewReason?: string | null | undefined
  /** Код, почему фон убран или не убирался (`PhotoBackgroundReasons`, RP-FB62); нет поля — прежний сервер. */
  backgroundReason?: string | null | undefined
  /** Режим подготовки, в котором сейчас индекс и запрос: `none` | `background-removed`. */
  indexMode?: string | null | undefined
  /** Сколько товаров с фото индекс уже пересчитал под настроенный режим и сколько их всего; `null` — не читалось. */
  indexRebuildDone?: number | null | undefined
  indexRebuildTotal?: number | null | undefined
  /** Запрос считался дважды — по исходному кадру и по кадру рамки товара (индекс смешанный). */
  dualQuery?: boolean | null | undefined
  /** Какой вариант дал лучшее сходство: `original` | `background-removed`. */
  bestVariant?: string | null | undefined
  originalScore?: number | null | undefined
  cutScore?: number | null | undefined
  /** Рамка товара от детектора в ДОЛЯХ (0…1) исходного снимка после поворота по EXIF `[x0, y0, x1, y1]`; `null` — рамки нет (см. `productBoxStatus`). */
  productBox?: readonly number[] | null | undefined
  /** Итог поиска рамки: `applied` — найдена; `skipped-no-box` — детектор товар не нашёл, в модель ушёл кадр целиком; `null` — не искалась или прежний сервер. */
  productBoxStatus?: string | null | undefined
}

/**
 * Вердикт сервера по снимку (RP-FB56): один из четырёх ответов. Считает сервер, клиент его не пересчитывает (иначе пороги были бы в двух
 * местах). `undetermined` — «не удалось определить»: это НЕ «нет в базе».
 */
export const SEARCH_VERDICTS = ['found', 'candidates', 'not-found', 'undetermined'] as const
export type SearchVerdict = (typeof SEARCH_VERDICTS)[number]

export const isSearchVerdict = (value: string): value is SearchVerdict => SEARCH_VERDICTS.some((verdict) => verdict === value)

/** Слово, прочитанное на снимке, и его полоса веса: по подтверждению «Это он» становится поисковой фразой товара. */
export interface HintWord {
  text: string
  band: LineBand
}

/** Подсказки для «Создания» и «Прикрепления»: название из ключевых строк, штрихкод с верной цифрой, прочитанные слова. */
export interface SearchHints {
  suggestedName: string | null
  barcode: string | null
  /** Контрольная цифра штрихкода сошлась. */
  barcodeVerified: boolean
  words: readonly HintWord[]
}

/** Группы ответа: уверенные («Это он?») и возможные («Возможно, это»). Ниже порога в них ничего нет — лучшие из отсечённых отдельно, в `weak`. */
export interface SearchGroups {
  confident: readonly SearchHit[]
  possible: readonly SearchHit[]
  /** Сколько позиций отсечено порогом. */
  belowThreshold: number
  /** Лучшие из отсечённых (по убыванию совпадения): выводятся ВСЕГДА списком «ниже порога — вероятнее всего не он» (RP-FB62). */
  weak?: readonly SearchHit[] | undefined
  /** Нижняя граница списка 0…1: ниже неё позиция «не принята». */
  listMin?: number | null | undefined
}

/** Причина вердикта (`VerdictReason`): код (`similar-variants`, `text-photo-disagree`, `text-only`, `candidates`…) и числа для подстановки; фраз сервер не присылает. */
export interface VerdictReason {
  code: string
  params: Readonly<Record<string, string>>
}

export interface SearchResponse {
  scope: string
  total: number
  hits: readonly SearchHit[]
  /** Позиции индекса без товара Platform: неполнота данных, а не «ничего нет». */
  unresolved: number
  /** `null` — снимок не читали: служба распознавания текста не ответила или его не умеет. */
  recognized: PhotoRecognition | null
  /** Контраст выдачи по фото 0…1: около 0 — кандидаты почти одинаковы и фото «не различает»; `null` — кандидатов мало. */
  visualContrast: number | null
  /** Как обработан кадр перед энкодером; `null` — прежний сервер. */
  processing: PhotoProcessing | null
  /** Вердикт (RP-FB56); `null`/нет — сервер прежний, экран показывает плоскую выдачу `hits`. */
  verdict?: SearchVerdict | null | undefined
  groups?: SearchGroups | undefined
  hints?: SearchHints | null | undefined
  /** Версия правил вердикта и числа порогов — для «Тестирования». */
  verdictStamp?: string | null | undefined
  /** Причины вердикта (RP-FB56/FB65); нет поля — сервер прежний. */
  reasons?: readonly VerdictReason[] | undefined
  /** Номер поиска в журнале признаков (RP-FB77): возвращается с действием человека; `null` — поиск не записан. */
  searchId?: string | null | undefined
}

export interface SearchHealth {
  status: string
  components: readonly { name: string; status: string; detail: string | null }[]
}

export interface HitVm {
  productId: string
  name: string
  sku: string | null
  barcode: string | null
  /** Процент совпадения целым числом 0…100 для угла плашки; `null` — числа нет (не число с сервера). */
  matchPercent: number | null
  matchedBy: string
  identities: readonly HitIdentityVm[]
  /** Процент для показа шагом 5 (вниз к 5; 100 — только при полном совпадении), RP-FB66; `null` — числа нет. */
  stepPercent?: number | null | undefined
  /** Что сказало фото; `null`/нет — сервер не сообщил, подписи нет. */
  photoCheck?: PhotoCheck | null | undefined
  /** Миниатюра главного фото ОБЩЕГО товара (адрес у Platform); `null`/нет — своих фото нет, показывается запасное фото источника. */
  commonPhotoUrl?: string | null | undefined
  /** Свидетельства уверенности для списка «Из чего сложилось»; пусто — сервер прежний. */
  evidence?: readonly EvidenceVm[] | undefined
  /** Серия близнецов позиции (RP-FB65): `id` общий у членов, `differences` — чем эта позиция отличается от соседей; `null`/нет — не в серии. */
  twinGroup?: TwinGroup | null | undefined
  /** Текст и фото расходятся: `text` — позицию назвал текст, `photo` — фото, `unknown` — полей для разбора мало; `null`/нет — не расходятся. */
  disagreement?: Disagreement | null | undefined
}

/** С чьей стороны позиция в расхождении «текст ≠ фото» (RP-FB65): выводится из свидетельств, сервер называет только сам факт. */
export type Disagreement = 'text' | 'photo' | 'unknown'

/** Влияние свидетельства на уверенность: `for` — довод «за», `against` — «против», `neutral` — учтено, но не сдвинуло, `none` — данных нет (не минус). */
export type EvidenceEffect = 'for' | 'against' | 'neutral' | 'none'

/** Свидетельство к показу: название дорожки и пояснение по коду; `present: false` — «нет данных». */
export interface EvidenceVm {
  kind: string
  label: string
  /** Пояснение словами по коду свидетельства; новый код сервера показывается как есть, `null` — пояснять нечего. */
  note: string | null
  present: boolean
  effect: EvidenceEffect
}

export interface HitIdentityVm {
  key: string
  resourceId: string
  resourceKind: string
  resource: string
  resourceKnown: boolean
  remoteKey: string
  state: string | null
}

export type { OpenCommonProduct, ProductPhotoUrl, ProductRef } from '../../../shared/product'

/** Итог поиска: «не нашлось» и «не смогли искать» — РАЗНЫЕ состояния. */
export type SearchOutcomeVm =
  | { kind: 'idle' }
  /** Поиск идёт. `preliminary` — предварительная выдача (список до сверки ИИ), если сервер её уже прислал. */
  | { kind: 'searching'; preliminary?: VerdictOutcomeVm | undefined }
  /** Человек отменил поиск: результата нет, и это не сбой. */
  | { kind: 'cancelled' }
  | VerdictOutcomeVm
  | {
      kind: 'failed'
      /** 503: служба поиска/inference недоступна — результата нет, это не «пусто». */
      degraded: boolean
      title: string
      message: string
      code: string
      correlationId: string | null
      /** Можно ли повторить тот же запрос: служба недоступна, сеть, таймаут. Отказ по существу (нет доступа, фото не принято) повтором не лечится. */
      retryable: boolean
      /** Предварительная выдача, пришедшая до сбоя: она не стирается, а остаётся с причиной, почему не уточнена. */
      preliminary?: VerdictOutcomeVm | undefined
    }

/** Итог поиска с вердиктом сервера (RP-FB56): экран «Это он? / Возможно / Нет в базе / Не удалось определить». */
export interface VerdictOutcomeVm {
  kind: 'verdict'
  verdict: SearchVerdict
  /** Позиции «Это он?»; у вердикта, кроме `found`, пусто. */
  confident: readonly HitVm[]
  /** Позиции «Возможно, это», по убыванию уверенности. */
  possible: readonly HitVm[]
  /** Слабые кандидаты ниже порога: выводятся ВСЕГДА открытым списком под вердиктом, чтобы человек мог выбрать вручную (RP-FB62). */
  weak?: readonly HitVm[] | undefined
  /** Нижняя граница списка в процентах; `null` — сервер не сообщил. */
  listMinPercent?: number | null | undefined
  hints: SearchHints | null
  total: number
  unresolved: number
  recognized: PhotoRecognition | null
  /** Варианты серии близнецов (≥ 2) с отличиями — экран «Выберите вариант» (RP-FB66); нет — обычный вердикт. */
  twin?: readonly HitVm[] | undefined
  /** Причины вердикта от сервера (коды и числа); нет — сервер прежний. */
  reasons?: readonly VerdictReason[] | undefined
  /** Номер поиска в журнале признаков (RP-FB77): с ним действия человека становятся метками для обучения; `null` — поиск не записан. */
  searchId?: string | null | undefined
}

export type HealthLevel = 'unknown' | 'checking' | 'ok' | 'degraded' | 'not-configured' | 'unreachable' | 'forbidden'

export interface HealthVm {
  level: HealthLevel
  label: string
  detail: string | null
  components: readonly { name: string; label: string; detail: string | null }[]
}

export interface SearchVm {
  /** Видны ли служебные звенья и проверка состояния (право `platform:search:test`, RP-FB2). */
  canDiagnose: boolean
  /** Роль «Администратор приложения» (`system_admin`): только ей видна ссылка «Посмотреть детали поиска» под итогом. */
  isSystemAdmin: boolean
  health: HealthVm
  outcome: SearchOutcomeVm
}

/**
 * Действия над ответом с вердиктом (RP-FB56). Их даёт binding: открытие окон, счётчик сессии и право менять общий каталог.
 * «Снять ещё раз» экран делает сам (камера или выбор файла), а binding только узнаёт, что кадр пропущен.
 */
export interface VerdictActions {
  /** Есть ли право менять общий каталог: без него «Создать» и «Прикрепить» не показываются, вердикт виден. */
  canWrite: boolean
  /** «Это он — открыть»: окно общего товара. */
  openHit: (hit: HitVm) => void
  /**
   * «Да, это он» / «Это он»: человек подтвердил товар. С правом менять каталог система ЗАПОМИНАЕТ снимок именно за этим товаром (фото
   * прикрепляется, прочитанный текст становится его поисковыми фразами); без права — просто открывает карточку.
   */
  confirmHit: (hit: HitVm) => void
  /** «Нет, другой»: предложенные в «Это он?» товары — не тот (метки для обучения поиска, RP-FB77). Подсказку прячет сам экран. */
  rejectOffered: (hits: readonly HitVm[]) => void
  /** «Ничего из этого»: среди показанного товара нет (метки для обучения поиска). Переход к «Нет в базе» экран делает сам. */
  noneOfThese: () => void
  /** «Создать новый товар» (и «Создать всё равно»): окно «Новый общий товар» с кадром, названием и штрихкодом. */
  createNew: () => void
  /** «Найти товар и прикрепить фото»: выбор товара общего списка. */
  findAndAttach: () => void
  /** Человек ушёл без действия: «Снять ещё раз» или «Ничего из этого» — в счётчик «Пропущено». */
  skipped: () => void
}

export interface SearchActions {
  search: (photo: File) => void
  /** Действия над вердиктом; не заданы — ответ показан, но кнопок «Открыть/Создать/Прикрепить» нет. */
  verdict?: VerdictActions | undefined
  /** Прервать идущий поиск: загрузка обрывается, сервер прекращает работу, где может. */
  cancel?: () => void
  /** Повторить поиск по тому же кадру, не снимая и не выбирая заново. */
  retry?: () => void
  checkHealth: () => void
  /** Открыть окно сканера (RP-FB4); не задано — кнопки нет. */
  openScanner?: () => void
}

/** Окно сканера (RP-FB4): что дал штрихкод с камеры и что дал снимок. Свежее заменяет прежнее. */
export interface ScannerVm {
  /** Номер последнего сканирования: новый номер заново открывает лист результата, даже скрытый и даже для того же кода. */
  scanId: number
  lookup: BarcodeLookupVm
  photo: SearchOutcomeVm
}

export interface ScannerActions {
  /** Действия над вердиктом (как на экране поиска). */
  verdict?: VerdictActions | undefined
  cancel?: () => void
  retry?: () => void
  /** Штрихкод с потока камеры — в общий каталог. Экран зовёт только на НОВОЕ сканирование (другой код или тот же после ухода из кадра). */
  code: (code: string) => void
  /** Кадр с видео или выбранный снимок — в поиск по фото. */
  photo: (photo: File) => void
  close: () => void
}

/** Исход этапа в записи времени поиска. */
export type StageOutcome = 'ok' | 'failed' | 'skipped'

const STAGE_OUTCOMES: readonly string[] = ['ok', 'failed', 'skipped']
export const isStageOutcome = (value: string): value is StageOutcome => STAGE_OUTCOMES.includes(value)

/** Время одного этапа поиска: начало от старта и длительность, мс; пропущенный этап длится 0. */
export interface SearchStageTiming {
  stage: string
  startMs: number
  durationMs: number
  outcome: StageOutcome
}

/** Время одного боевого поиска: до итога и до первого показанного списка, мс, и по этапам. */
export interface SearchTiming {
  searchId: string
  startedAt: string
  totalMs: number
  firstListMs: number
  stages: readonly SearchStageTiming[]
}

/** Медиана, p90 и максимум по `count` замерам, мс. */
export interface TimingStats {
  count: number
  medianMs: number
  p90Ms: number
  maxMs: number
}

export interface SearchTimingSummary {
  from: string
  to: string
  searches: number
  /** Поисков в периоде больше, чем учтено: сводка по последним. */
  truncated: boolean
  total: TimingStats | null
  firstList: TimingStats | null
  stages: readonly { stage: string; stats: TimingStats }[]
}

/** Состояние загрузки одного куска времени поиска для показа: ждём, записи нет, сбой словами сервера или готово. */
export type TimingsLoad<T> =
  | { status: 'loading' }
  | { status: 'missing' }
  | { status: 'error'; message: string; correlationId: string | null }
  | { status: 'ready'; data: T }
